import {Filter, orderBy} from '@/library/filter';
import {isComponentName} from '@/library/utils';
import {deepCopyObject, XHR_STATUS_DEFAULT} from '@/utils/portuna';
import axios, {type CancelTokenSource} from 'axios';
import {reactive, unref} from 'vue';
import {Component, Inject, Prop, Vue, Watch} from 'vue-facing-decorator';
import dateHelper from '../utils/portuna/dateHelper';

interface FetchListPostParams
{
    buildTime: string
    search: {}
    usePager: number
    page: number
    perPage: number
    listIdentifier: Nullable<string>
    deleteList?: Nullable<string>
    orderBy?: { key: string | string[], direction: string }
}

interface FetchListOptions {
    addEntries: boolean
}

@Component({
    name: 'ListAbstract',
    emits: ['entries-fetched', 'entries-fetch-failed', 'search-changed'],
})
export default class ListAbstract extends Vue
{
    @Prop({
        required: false,
        validator(value: any[] | null)
        {
            return Array.isArray(value) || value === null;
        },
        default()
        {
            return reactive([]);
        },
    }) entries: any[];

    @Prop({
        type: [Filter, Boolean],
    }) filter: Filter | false;

    @Prop({
        type: Object,
        required: false,
        default()
        {
            return reactive({});
        },
    }) search: SearchDefinition;

    @Prop({
        type: String,
        default: '',
        required: false,
    }) fetchUrl: string;

    @Prop({
        type: [String, Boolean],
        default: '',
        required: false,
    }) localStorageScope: string | false;

    @Prop({
        required: false,
        validator(value: string)
        {
            return ['client', 'server'].includes(value);
        },
        default: 'client',
    }) filterMethod: 'client' | 'server';

    @Prop({
        type: Array,
        required: false,
        default: null,
    }) ignoreFilters: Nullable<string[]>;

    @Prop({
        type: Function,
        required: false,
        default: null,
    }) customFilter: Nullable<(entry: any) => boolean>;

    @Prop({
        type: Boolean,
        default: false,
        required: false,
    }) searchRecursive: boolean;

    @Prop({
        type: Boolean,
        default: true,
        required: false,
    }) showPager: boolean;

    @Prop({
        type: [String, Array],
        default: '',
        required: false,
    }) sortField: string | string[];

    @Prop({
        type: String,
        default: 'asc',
        required: false,
    }) orderBy: FilterOrder;

    @Prop({
        type: Number,
        default: 25,
        required: false,
    }) defaultEntriesPerPage: number;

    @Prop({
        type: Function,
        default: (entriesPerPage: number) =>
        {
            return;
        },
    }) setEntriesPerPage: (entriesPerPage: number) => void;

    entriesPerPage: number = 25;

    initializeLoading: boolean = true;

    loading: boolean = false;

    lastReload: number = Date.now();

    page: number = 1;

    // todo global - create computed totalCount || entries.length
    totalCount: Nullable<number> = null;

    // todo global - create computed filteredCount || filteredEntries.length
    filteredCount: Nullable<number> = null;

    fetchListBuildTime: string = dateHelper('Y-m-d H:i:s') as string;

    fetchListIdentifier: Nullable<string> = null;

    oldFetchListIdentifier: Nullable<string> = null;

    loadingXhrSource: Nullable<CancelTokenSource> = null;

    defaultFetchListOptions: FetchListOptions = {
        addEntries: true
    };

    isListAbstract = true;

    previousSearch: string = '';

    lastSortField: string | string[] = '';

    lastOrderBy: string = '';

    created()
    {
        this.setDefaultPage();

        this.initializeLoading = false;

        this.lastOrderBy = this.orderBy;
        this.lastSortField = this.sortField;
        this.entriesPerPage = this.defaultEntriesPerPage;

        if ('server' === this.filterMethod && !isComponentName(this, 'ListInfiniteScroll'))
        {
            this.fetchList().catch(this.catchFetchList);
        }

        this.previousSearch = JSON.stringify(this.search);
    }

    beforeUnmount()
    {
        this.removeListIdentifier();
    }

    @Inject({
        default()
        {
            return (entries: any[]) => entries;
        },
    }) readonly postParseEntries: (entries: any[]) => any[];

    @Watch('fetchUrl')
    rebuildOnFetchUrlChange()
    {
        this.rebuild(true);
    }

    @Watch('search', {deep: true})
    watchSearchAndFetchList()
    {
        const search = JSON.stringify(this.search);

        if (this.previousSearch === search)
        {
            return;
        }

        this.previousSearch = search;

        if ('server' === this.filterMethod)
        {
            this.rebuild();
        }

        this.$emit('search-changed');
    }

    @Watch('entriesPerPage', {deep: true})
    watchEntriesPerPage(entriesPerPage: number)
    {
        this.setEntriesPerPage(entriesPerPage);
    }

    @Watch('localStorageScope')
    watchLocalStorageScope()
    {
        this.initializeLoading = true;
        this.initializeLoading = false;
    }

    get entriesCount()
    {
        if (null !== this.totalCount)
        {
            return this.totalCount;
        }
        else
        {
            return this.filteredEntries.length;
        }
    }

    get entryList()
    {
        return this.entries;
    }

    get listEntries()
    {
        return this.entryList;
    }

    get filterObj()
    {
        return this.getFilterObject();
    }

    get filteredEntries(): any[]
    {
        if ('server' === this.filterMethod)
        {
            return this.entries;
        }

        let entries = this.entryList;
        let filter  = this.filterObj;

        if (filter instanceof Filter)
        {
            if (this.searchRecursive)
            {
                let matches: any[] = [];

                this.filterRecursive(filter, entries, matches);

                entries = matches;
            }
            else
            {
                entries = entries.filter(entry => {
                    if (this.customFilter && !this.customFilter(entry))
                    {
                        return false;
                    }

                    return filter!.run(entry);
                });
            }
        }
        else if(this.customFilter)
        {
            entries = entries.filter(entry => {
                return this.customFilter!(entry);
            });
        }

        return entries;
    }

    get sortedEntries()
    {
        if ('server' === this.filterMethod)
        {
            return this.postParseEntries(this.entries);
        }

        if (this.sortField.length === 0)
        {
            return this.filteredEntries;
        }

        return this.postParseEntries(orderBy(this.filteredEntries, this.sortField, this.orderBy));
    }

    get postParams(): FetchListPostParams
    {
        let postData: FetchListPostParams = {
            buildTime: this.fetchListBuildTime,
            search: unref(this.search),
            usePager: this.showPager ? 1 : 0,
            page: this.page,
            perPage: this.entriesPerPage,
            listIdentifier: this.fetchListIdentifier,
        };

        if (this.oldFetchListIdentifier)
        {
            postData.deleteList         = this.oldFetchListIdentifier;
            this.oldFetchListIdentifier = null;
        }

        if (this.sortField)
        {
            postData.orderBy = {
                key: this.sortField,
                direction: this.orderBy,
            };
        }

        return postData;
    }

    filterRecursive(filter: Filter, entries: any[], matches: any[])
    {
        for (let entry of entries)
        {
            if (filter?.run(entry))
            {
                matches.push(entry);
            }

            if (entry.children)
            {
                this.filterRecursive(filter, entry.children, matches);
            }
        }
    }

    catchFetchList(error: Error)
    {
        if (axios.isCancel(error))
        {
            console.log('Request canceled: ', error.message);
        }
        else
        {
            this.$emit('entries-fetch-failed', error);
            this.loadingXhrSource = null;
            this.loading          = false;
        }
    }

    fetchList(data: Record<string, any> = {}, options: Partial<FetchListOptions> = {})
    {
        options = Object.assign({}, this.defaultFetchListOptions, options);

        this.loading          = true;
        this.loadingXhrSource = axios.CancelToken.source();

        let xhr = this.axios.post(this.fetchUrl, Object.assign({}, this.postParams, data), {
            cancelToken: this.loadingXhrSource.token,
        });

        xhr.then(({data: responseData}) =>
        {
            if (XHR_STATUS_DEFAULT !== responseData.status)
            {
                throw responseData;
            }

            const data = responseData.data;

            if (data.entries.length)
            {
                options.addEntries && this.addEntries(data.entries);
            }

            this.fetchListIdentifier = data.identifier;

            // @deprecated, use @provide postParseEntries
            this.$emit(
                'entries-fetched',
                {
                    allEntries: this.entries || [],
                    requestEntries: data.entries || [],
                    count: data.count || this.entries.length || 0,
                    filteredCount: data.filteredCount || this.filteredEntries.length || 0,
                },
            );

            if ('undefined' !== typeof data.count)
            {
                this.totalCount    = data.count;
                this.filteredCount = data.filteredCount;
            }

            this.loadingXhrSource = null;
            this.loading          = false;
        })
        .catch(this.catchFetchList);

        return xhr;
    }

    addEntries(entries: object[], toStart = false)
    {
        if (toStart)
        {
            this.entries.unshift(...entries);
        }
        else
        {
            this.entries.push(...entries);
        }
    }

    getFilterObject(): Nullable<Filter>
    {
        if ('server' === this.filterMethod)
        {
            return null;
        }
        console.debug('[ListAbstract] generate new filter obj', Math.random());
        let search = deepCopyObject(this.search);

        this.rebuild();

        if (this.ignoreFilters && this.ignoreFilters.length)
        {
            for (const filterName of this.ignoreFilters)
            {
                delete search[filterName];
            }
        }

        return this.filter || search && Filter.createByObject(search) || null;
    }

    @Watch('sortField')
    @Watch('orderBy')
    watchSort()
    {
        this.emitter.emit('setSort', this.sortField, this.orderBy);
        if ('server' === this.filterMethod && (this.lastSortField !== this.sortField || this.lastOrderBy !== this.orderBy))
        {
            this.lastSortField = this.sortField;
            this.lastOrderBy = this.orderBy;
            this.rebuild();
        }
        console.debug('[ListAbstract] sort by', this.sortField, this.orderBy);
    }

    setDefaultPage()
    {
        this.page = 1;
    }

    /**
     *
     * @param resetWholeList if true, also reset fetchListIdentifier for ListCache
     * @param resetPage reset current page
     */
    rebuild(resetWholeList: boolean = false, resetPage: boolean = true)
    {
        if (this.initializeLoading)
        {
            return;
        }

        if (this.loadingXhrSource)
        {
            this.loadingXhrSource.cancel('canceled by rebuild');
        }

        if (resetWholeList)
        {
            this.oldFetchListIdentifier = this.fetchListIdentifier;
            this.fetchListIdentifier    = null;
        }

        if (resetWholeList || 'server' === this.filterMethod)
        {
            this.entries.splice(0, this.entries.length);
            this.totalCount = 0;
        }

        resetPage && this.setDefaultPage();
        this.lastReload = Date.now();
    }

    removeListIdentifier()
    {
        if (this.fetchListIdentifier)
        {
            // do not implement an error handler - just a cleaner
            this.axios.post('/application/ajax/delete-list', {deleteList: this.fetchListIdentifier}).then();
        }
    }
}